import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Observable, map } from 'rxjs';
import { API_BASE_URL } from '@core/config/api.config';
import { ApiEndpoints } from '@core/config/api-endpoints';
import {
  PaginatedResult,
  PaginationParams,
  createPaginatedResult,
} from '@core/common/models/pagination.models';
import { PreparationSettingsApiInput } from '@shared/ui/preparation-editor/preparation-editor.models';
import { mapPreparationApiInputToCategoryMutationFields } from '@shared/ui/preparation-editor/preparation-editor.utils';
import { CategoryMutationResult, ProductCategory } from '@features/catalog/models/product-category.models';
import {
  GraphqlMutationCategoryPayloadDto,
  GraphqlProductCategoryByIdDataDto,
} from './dto/product-category.dto';
import { ensureNoTopLevelErrors, GraphqlHttpResponse, mapEntityMutationResult, mapLifecycleMutationResult } from './mappers/graphql-error.mapper';
import {
  asDtoArray,
  mapMutationPayload,
  mapProductCategoryDto,
} from './mappers/product-category.mapper';

/**
 * Lista plana (`flat: true`): el API devuelve todas las categorías con `parentId`, sin tope de profundidad.
 * La pantalla arma el árbol en cliente (`toHierarchicalRows`).
 */
const GET_PRODUCT_CATEGORIES_QUERY = `
  query GetProductCategoriesPage(
    $activeOnly: Boolean = true,
    $parentId: ID,
    $flat: Boolean = true,
    $limit: Int = 50,
    $offset: Int = 0
  ) {
    productCategories(
      activeOnly: $activeOnly,
      parentId: $parentId,
      flat: $flat,
      limit: $limit,
      offset: $offset
    ) {
      items {
        id
        name
        slug
        parentId
        position
        active
        iconName
        productsCount
        fulfillmentLeadTimeMode
        fulfillmentLeadTimeDays
        prepRequiresMode
        prepRequires
        prepTimeMinutes
      }
      total
      limit
      offset
    }
  }
`;

const PRODUCT_CATEGORY_BY_ID_QUERY = `
  query ProductCategoryById($id: ID!) {
    productCategory(id: $id) {
      id
      name
      slug
      parentId
      active
      position
      fulfillmentLeadTimeMode
      fulfillmentLeadTimeDays
      subcategories {
        id
        name
      }
      products {
        id
        name
      }
    }
  }
`;

const PRODUCT_CATEGORY_BY_NAME_QUERY = `
  query ProductCategoryByName($name: String!) {
    productCategory(name: $name) {
      id
      name
      slug
      parentId
      active
      position
      fulfillmentLeadTimeMode
      fulfillmentLeadTimeDays
      subcategories {
        id
        name
      }
      products {
        id
        name
      }
    }
  }
`;

/**
 * Mutaciones alineadas con schema graphql-ruby típico: argumentos sueltos, sin tipo `*Input` envuelto.
 * El slug lo suele generar el servidor.
 */
const CREATE_MUTATION = `
  mutation CreateProductCategory(
    $name: String!,
    $position: Int,
    $parentId: ID,
    $active: Boolean,
    $iconName: String,
    $fulfillmentLeadTimeMode: String,
    $fulfillmentLeadTimeDays: Int
  ) {
    createProductCategory(
      name: $name,
      position: $position,
      parentId: $parentId,
      active: $active,
      iconName: $iconName,
      fulfillmentLeadTimeMode: $fulfillmentLeadTimeMode,
      fulfillmentLeadTimeDays: $fulfillmentLeadTimeDays
    ) {
      category {
        id
        name
        slug
        parentId
        active
        iconName
        productsCount
        fulfillmentLeadTimeMode
        fulfillmentLeadTimeDays
        prepRequiresMode
        prepRequires
        prepTimeMinutes
      }
      errors
    }
  }
`;

const UPDATE_MUTATION = `
  mutation UpdateProductCategory(
    $id: ID!,
    $name: String,
    $active: Boolean,
    $position: Int,
    $parentId: ID,
    $iconName: String,
    $fulfillmentLeadTimeMode: String,
    $fulfillmentLeadTimeDays: Int,
    $preparationSettings: PreparationSettingsInput
  ) {
    updateProductCategory(
      id: $id,
      name: $name,
      active: $active,
      position: $position,
      parentId: $parentId,
      iconName: $iconName,
      fulfillmentLeadTimeMode: $fulfillmentLeadTimeMode,
      fulfillmentLeadTimeDays: $fulfillmentLeadTimeDays,
      preparationSettings: $preparationSettings
    ) {
      category {
        id
        name
        slug
        parentId
        active
        iconName
        productsCount
        fulfillmentLeadTimeMode
        fulfillmentLeadTimeDays
        prepRequiresMode
        prepRequires
        prepTimeMinutes
      }
      errors
    }
  }
`;

const DESTROY_MUTATION = `
  mutation DestroyProductCategory($id: ID!) {
    destroyProductCategory(id: $id) {
      category {
        id
        name
        active
      }
      errors
    }
  }
`;

const PERMANENTLY_DELETE_MUTATION = `
  mutation PermanentlyDeleteProductCategory($id: ID!) {
    permanentlyDeleteProductCategory(id: $id) {
      success
      errors
    }
  }
`;

const MOVE_MUTATION = `
  mutation MoveProductCategory($id: ID!, $position: Int!, $parentId: ID) {
    moveProductCategory(id: $id, position: $position, parentId: $parentId) {
      category {
        id
        name
        position
        parentId
      }
      errors
    }
  }
`;

const BULK_UPDATE_CATEGORIES_PREPARATION_MUTATION = `
  mutation UpdateProductCategoriesPreparation(
    $categoryIds: [ID!]!
    $settings: PreparationSettingsInput!
  ) {
    updateProductCategoriesPreparation(categoryIds: $categoryIds, settings: $settings) {
      categories { id }
      errors
    }
  }
`;

const SET_CATEGORY_HALF_AND_HALF_MUTATION = `
  mutation SetProductCategoryHalfAndHalf($categoryId: ID!, $enabled: Boolean!) {
    setProductCategoryHalfAndHalf(categoryId: $categoryId, enabled: $enabled) {
      updatedCount
      errors
    }
  }
`;

function mergeCategoryPreparationVariables(
  input: PreparationSettingsApiInput & {
    fulfillmentLeadTimeMode?: 'inherit' | 'override' | null;
    fulfillmentLeadTimeDays?: number | null;
  }
): Record<string, unknown> {
  const fromPreparation = mapPreparationApiInputToCategoryMutationFields(input);
  const variables: Record<string, unknown> = {
    ...(input.fulfillmentLeadTimeMode !== undefined
      ? { fulfillmentLeadTimeMode: input.fulfillmentLeadTimeMode }
      : fromPreparation.fulfillmentLeadTimeMode !== undefined
        ? { fulfillmentLeadTimeMode: fromPreparation.fulfillmentLeadTimeMode }
        : {}),
    ...(input.fulfillmentLeadTimeDays !== undefined
      ? { fulfillmentLeadTimeDays: input.fulfillmentLeadTimeDays }
      : fromPreparation.fulfillmentLeadTimeDays !== undefined
        ? { fulfillmentLeadTimeDays: fromPreparation.fulfillmentLeadTimeDays }
        : {}),
  };

  const preparationSettings: PreparationSettingsApiInput = {};
  if (input.prepRequiresMode !== undefined) {
    preparationSettings.prepRequiresMode = input.prepRequiresMode;
  }
  if (input.requiresPrep !== undefined) {
    preparationSettings.requiresPrep = input.requiresPrep;
  }
  if (input.timeKind !== undefined) {
    preparationSettings.timeKind = input.timeKind;
  }
  if (input.prepTimeMinutes !== undefined) {
    preparationSettings.prepTimeMinutes = input.prepTimeMinutes;
  }
  if (input.fulfillmentReadyTime !== undefined) {
    preparationSettings.fulfillmentReadyTime = input.fulfillmentReadyTime;
  }

  if (Object.keys(preparationSettings).length > 0) {
    variables['preparationSettings'] = preparationSettings;
  }

  return variables;
}

@Injectable()
export class CategoryApiService {
  constructor(
    private readonly http: HttpClient,
    @Inject(API_BASE_URL) private readonly baseUrl: string
  ) {}

  private graphqlUrl(): string {
    return `${this.baseUrl}${ApiEndpoints.graphql}`;
  }

  list(
    options?: { activeOnly?: boolean | null },
    pagination?: PaginationParams
  ): Observable<PaginatedResult<ProductCategory>> {
    const limit = pagination?.limit ?? 50;
    const offset = pagination?.offset ?? 0;
    const activeOnly = options?.activeOnly === undefined ? true : options.activeOnly;
    return this.http
      .post<GraphqlHttpResponse<{ productCategories: { items: any[]; total: number; limit: number; offset: number } }>>(
        this.graphqlUrl(),
        {
          query: GET_PRODUCT_CATEGORIES_QUERY,
          variables: {
            activeOnly,
            parentId: null,
            flat: true,
            limit,
            offset,
          },
        }
      )
      .pipe(
        map((res) => ensureNoTopLevelErrors(res, { allowDataWithTopLevelErrors: true })),
        map((data) => {
          const categories = (data.productCategories?.items ?? []).map(mapProductCategoryDto);
          return createPaginatedResult({
            items: categories,
            total: data.productCategories?.total ?? 0,
            limit: data.productCategories?.limit ?? limit,
            offset: data.productCategories?.offset ?? offset,
          });
        })
      );
  }

  searchByName(name: string): Observable<PaginatedResult<ProductCategory>> {
    const term = name.trim();
    if (!term) {
      return this.list();
    }
    return this.http
      .post<GraphqlHttpResponse<GraphqlProductCategoryByIdDataDto>>(this.graphqlUrl(), {
        query: PRODUCT_CATEGORY_BY_NAME_QUERY,
        variables: { name: term },
      })
      .pipe(
        map((res) => ensureNoTopLevelErrors(res, { allowDataWithTopLevelErrors: true })),
        map((data) => {
          const items = asDtoArray(data.productCategory).map(mapProductCategoryDto);
          return createPaginatedResult({
            items,
            total: items.length,
            limit: 50,
            offset: 0,
          });
        })
      );
  }

  getById(id: string): Observable<ProductCategory | null> {
    return this.http
      .post<GraphqlHttpResponse<GraphqlProductCategoryByIdDataDto>>(this.graphqlUrl(), {
        query: PRODUCT_CATEGORY_BY_ID_QUERY,
        variables: { id },
      })
      .pipe(
        map((res) => ensureNoTopLevelErrors(res, { allowDataWithTopLevelErrors: true })),
        map((data) => {
          const list = asDtoArray(data.productCategory);
          return list[0] ? mapProductCategoryDto(list[0]) : null;
        })
      );
  }

  create(input: {
    name: string;
    position: number;
    parentId?: string | null;
    active?: boolean;
    iconName?: string | null;
    fulfillmentLeadTimeMode?: 'inherit' | 'override' | null;
    fulfillmentLeadTimeDays?: number | null;
  } & PreparationSettingsApiInput): Observable<CategoryMutationResult> {
    const mergedPreparation = mergeCategoryPreparationVariables(input);
    // createProductCategory no acepta preparationSettings ni args prep* planos.
    const { preparationSettings: _ignoredPreparationSettings, ...fulfillmentVariables } =
      mergedPreparation;
    return this.http
      .post<
        GraphqlHttpResponse<{
          createProductCategory?: GraphqlMutationCategoryPayloadDto | null;
        }>
      >(this.graphqlUrl(), {
        query: CREATE_MUTATION,
        variables: {
          name: input.name.trim(),
          position: input.position,
          parentId: input.parentId ?? null,
          ...(input.active !== undefined ? { active: input.active } : {}),
          iconName: input.iconName ?? null,
          ...fulfillmentVariables,
        },
      })
      .pipe(
        map((res) => ensureNoTopLevelErrors(res, { allowDataWithTopLevelErrors: true })),
        map((data) => mapMutationPayload(data.createProductCategory))
      );
  }

  update(input: {
    id: string;
    name?: string;
    active?: boolean;
    position?: number;
    parentId?: string | null;
    iconName?: string | null;
    fulfillmentLeadTimeMode?: 'inherit' | 'override' | null;
    fulfillmentLeadTimeDays?: number | null;
  } & PreparationSettingsApiInput): Observable<CategoryMutationResult> {
    const variables: Record<string, unknown> = { id: input.id };
    if (input.name !== undefined) {
      variables['name'] = input.name.trim();
    }
    if (input.active !== undefined) {
      variables['active'] = input.active;
    }
    if (input.position !== undefined) {
      variables['position'] = input.position;
    }
    if (input.parentId !== undefined) {
      variables['parentId'] = input.parentId;
    }
    if (input.iconName !== undefined) {
      variables['iconName'] = input.iconName;
    }
    Object.assign(variables, mergeCategoryPreparationVariables(input));
    return this.http
      .post<
        GraphqlHttpResponse<{
          updateProductCategory?: GraphqlMutationCategoryPayloadDto | null;
        }>
      >(this.graphqlUrl(), {
        query: UPDATE_MUTATION,
        variables,
      })
      .pipe(
        map((res) => ensureNoTopLevelErrors(res, { allowDataWithTopLevelErrors: true })),
        map((data) => mapMutationPayload(data.updateProductCategory))
      );
  }

  destroy(id: string): Observable<CategoryMutationResult> {
    return this.http
      .post<
        GraphqlHttpResponse<{
          destroyProductCategory?: GraphqlMutationCategoryPayloadDto | null;
        }>
      >(this.graphqlUrl(), {
        query: DESTROY_MUTATION,
        variables: { id },
      })
      .pipe(
        map((res) =>
          mapEntityMutationResult(res, 'destroyProductCategory', (payload) =>
            mapMutationPayload(payload as GraphqlMutationCategoryPayloadDto | null | undefined)
          )
        )
      );
  }

  updateActive(id: string, active: boolean): Observable<CategoryMutationResult> {
    return this.update({ id, active });
  }

  permanentlyDelete(id: string): Observable<{ success: boolean; errors: string[] }> {
    return this.http
      .post<
        GraphqlHttpResponse<{
          permanentlyDeleteProductCategory?: { success?: boolean; errors?: string[] } | null;
        }>
      >(this.graphqlUrl(), {
        query: PERMANENTLY_DELETE_MUTATION,
        variables: { id },
      })
      .pipe(map((res) => mapLifecycleMutationResult(res, 'permanentlyDeleteProductCategory')));
  }

  move(input: {
    id: string;
    position: number;
    parentId?: string | null;
  }): Observable<CategoryMutationResult> {
    return this.http
      .post<
        GraphqlHttpResponse<{
          moveProductCategory?: GraphqlMutationCategoryPayloadDto | null;
        }>
      >(this.graphqlUrl(), {
        query: MOVE_MUTATION,
        variables: {
          id: input.id,
          position: input.position,
          parentId: input.parentId ?? null,
        },
      })
      .pipe(
        map((res) =>
          mapEntityMutationResult(res, 'moveProductCategory', (payload) =>
            mapMutationPayload(payload as GraphqlMutationCategoryPayloadDto | null | undefined)
          )
        )
      );
  }

  bulkUpdatePreparation(
    ids: readonly string[],
    input: PreparationSettingsApiInput
  ): Observable<{ updatedCount: number; errors: string[] }> {
    return this.http
      .post<
        GraphqlHttpResponse<{
          updateProductCategoriesPreparation?: {
            categories?: Array<{ id?: string | null } | null> | null;
            errors?: string[] | null;
          } | null;
        }>
      >(this.graphqlUrl(), {
        query: BULK_UPDATE_CATEGORIES_PREPARATION_MUTATION,
        variables: { categoryIds: [...ids], settings: input },
      })
      .pipe(
        map((res) => ensureNoTopLevelErrors(res, { allowDataWithTopLevelErrors: true })),
        map((data) => {
          const payload = data.updateProductCategoriesPreparation;
          return {
            updatedCount: payload?.categories?.length ?? 0,
            errors: payload?.errors ?? [],
          };
        })
      );
  }

  /** Activa/desactiva mitad y mitad en los productos de la categoría y sus subcategorías. */
  setHalfAndHalf(
    categoryId: string,
    enabled: boolean
  ): Observable<{ updatedCount: number; errors: string[] }> {
    return this.http
      .post<
        GraphqlHttpResponse<{
          setProductCategoryHalfAndHalf?: {
            updatedCount?: number | null;
            errors?: string[] | null;
          } | null;
        }>
      >(this.graphqlUrl(), {
        query: SET_CATEGORY_HALF_AND_HALF_MUTATION,
        variables: { categoryId, enabled },
      })
      .pipe(
        map((res) => ensureNoTopLevelErrors(res, { allowDataWithTopLevelErrors: true })),
        map((data) => {
          const payload = data.setProductCategoryHalfAndHalf;
          return {
            updatedCount: payload?.updatedCount ?? 0,
            errors: payload?.errors ?? [],
          };
        })
      );
  }
}
